import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { FaBell } from "react-icons/fa";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../actions/notifications";
import { toastAlert } from "../lib/toastAlert";
import "./notification-bell.css";

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function NotificationBell({ socket }) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    const { status, result, unreadCount: unread } = await fetchNotifications({
      limit: 20,
    });
    if (!mounted.current) return;
    if (status) {
      setItems(result);
      setUnreadCount(unread);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    loadNotifications();
    return () => {
      mounted.current = false;
    };
  }, [loadNotifications]);

  useEffect(() => {
    if (!socket) return undefined;

    const onNotification = (payload) => {
      if (!payload) return;
      setItems((prev) => {
        const exists = prev.some((n) => n._id === payload._id);
        if (exists) return prev;
        return [payload, ...prev].slice(0, 20);
      });
      setUnreadCount((c) => c + 1);
      toastAlert("success", payload.title || "New notification");
    };

    socket.on("NOTIFICATION", onNotification);
    return () => socket.off("NOTIFICATION", onNotification);
  }, [socket]);

  const handleOpen = (next) => {
    setOpen(next);
    if (next) loadNotifications();
  };

  const handleItemClick = async (item) => {
    if (!item.read) {
      const { status, unreadCount: unread } = await markNotificationRead(item._id);
      if (status) {
        setUnreadCount(unread);
        setItems((prev) =>
          prev.map((n) => (n._id === item._id ? { ...n, read: true } : n))
        );
      }
    }
    setOpen(false);
    if (item.link) history.push(item.link);
  };

  const handleMarkAll = async () => {
    const { status } = await markAllNotificationsRead();
    if (status) {
      setUnreadCount(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  return (
    <Dropdown
      className="notification-bell-dropdown"
      show={open}
      onToggle={handleOpen}
      align="end">
      <Dropdown.Toggle
        variant="link"
        className="notification-bell-toggle"
        id="notification-dropdown">
        <FaBell />
        {unreadCount > 0 ? (
          <span className="notification-bell-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </Dropdown.Toggle>

      <Dropdown.Menu className="notification-bell-menu">
        <div className="notification-bell-menu__head">
          <span>Notifications</span>
          {unreadCount > 0 ? (
            <button type="button" className="btn btn-link btn-sm p-0" onClick={handleMarkAll}>
              Mark all read
            </button>
          ) : null}
        </div>
        <div className="notification-bell-menu__body">
          {loading && !items.length ? (
            <p className="notification-bell-empty">Loading…</p>
          ) : null}
          {!loading && !items.length ? (
            <p className="notification-bell-empty">No notifications yet</p>
          ) : null}
          {items.map((item) => (
            <button
              type="button"
              key={item._id}
              className={`notification-bell-item${
                item.read ? "" : " notification-bell-item--unread"
              } notification-bell-item--${item.type || "info"}`}
              onClick={() => handleItemClick(item)}>
              <span className="notification-bell-item__title">{item.title}</span>
              <span className="notification-bell-item__msg">{item.message}</span>
              <span className="notification-bell-item__time">
                {formatTime(item.createdAt)}
              </span>
            </button>
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default NotificationBell;
